import { useState } from 'react';
import {
  defaultInputKindForType,
  enabledRegisterFields,
  labelToClassName,
  normalizeFieldOptions,
  resolveOptionIconUrl,
} from '../utils/kioskLandingDefaults';
import {
  CUSTOMER_FIRST_PLAN_ID_KEY,
  CUSTOMER_FIRST_CREDIT_KEY,
  isCustomerFirstEnrolled,
} from '../utils/registerFormHelpers';
import CountryWhatsAppInput from '../../../shared/components/CountryWhatsAppInput';
import { DEFAULT_WHATSAPP_COUNTRY } from '../../../shared/utils/whatsappPhone';
import {
  isRegisterMobileComplete,
  mobileValidationError,
  normalizeRegisterMobileValue,
} from '../../shared/mobileDigits';
import { lookupNtliveCustomerByMobile } from '../../partner/utils/ntliveCustomerApi';
import { applyNtliveCustomerToRegisterValues } from '../utils/ntliveCustomerMapping';

const IconUser = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="8" r="3.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M5 19c1.8-4 5-6 7-6s5.2 2 7 6"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const IconCalendar = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <rect x="4" y="5" width="16" height="15" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
    <path d="M8 3v4M16 3v4M4 10h16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const IconNote = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M7 4h10v16H7z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <path d="M9 9h6M9 13h6M9 17h4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const IconToggle = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <rect x="3" y="8" width="18" height="8" rx="4" fill="none" stroke="currentColor" strokeWidth="1.5" />
    <circle cx="15" cy="12" r="2.5" fill="currentColor" />
  </svg>
);

const IconOptions = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="8" cy="8" r="2.25" fill="none" stroke="currentColor" strokeWidth="1.5" />
    <circle cx="16" cy="8" r="2.25" fill="none" stroke="currentColor" strokeWidth="1.5" />
    <circle cx="12" cy="16" r="2.25" fill="none" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

function FieldIcon({ inputKind }) {
  if (inputKind === 'date') return <IconCalendar />;
  if (inputKind === 'textarea') return <IconNote />;
  if (inputKind === 'yesno') return <IconToggle />;
  if (inputKind === 'options') return <IconOptions />;
  return <IconUser />;
}

/**
 * Renders only enabled fields from the common register builder config.
 * Customer First Plan uses Yes / No, then Plan ID and Plan credit limit.
 */
export default function RegisterFormFields({
  registerForm,
  values,
  setValues,
  enableNtliveMobileLookup = false,
  onCrmCustomerFound = null,
}) {
  const fields = enabledRegisterFields(registerForm);
  const [crmBusy, setCrmBusy] = useState(false);
  const [crmMessage, setCrmMessage] = useState('');

  const setCustom = (key, value) => {
    setValues((prev) => ({
      ...prev,
      custom: { ...(prev.custom || {}), [key]: value },
    }));
  };

  if (!fields.length) {
    return (
      <p className="fi-register__panel-meta">
        No register fields are configured yet. Add fields in Kiosk Register Form.
      </p>
    );
  }

  return (
    <div className="fi-register__grid">
      {fields.map((field) => {
        const label = field.label || '';
        const inputKind =
          field.type === 'gender'
            ? 'options'
            : defaultInputKindForType(field.type, field.inputKind);
        const value = values.custom?.[field.key] || '';
        const isMembershipTier =
          field.type === 'membershipTier' || field.key === 'membershipTier';
        const showPlanId = isMembershipTier && isCustomerFirstEnrolled(value);
        const planIdValue = values.custom?.[CUSTOMER_FIRST_PLAN_ID_KEY] || '';
        const creditValue = values.custom?.[CUSTOMER_FIRST_CREDIT_KEY] || '';
        const optionItems =
          inputKind === 'options'
            ? normalizeFieldOptions(field.options, field.type)
            : [];
        const fullWidth = inputKind === 'textarea' || inputKind === 'yesno' || inputKind === 'options';
        const crmClass = field.className || labelToClassName(label) || field.key;

        return (
          <label
            key={field.id}
            className={
              fullWidth ? 'fi-register__field fi-register__field--full' : 'fi-register__field'
            }
            data-crm-class={crmClass}
          >
            <span className="fi-register__field-label">{label}</span>
            {inputKind === 'yesno' ? (
              <>
                <div className="fi-register__yesno" role="group" aria-label={label}>
                  <button
                    type="button"
                    className={
                      isCustomerFirstEnrolled(value) || value === 'Yes'
                        ? 'fi-register__yesno-btn is-active'
                        : 'fi-register__yesno-btn'
                    }
                    onClick={() => setCustom(field.key, isMembershipTier ? 'Yes' : 'Yes')}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    className={
                      value === 'No' || value === 'Disable'
                        ? 'fi-register__yesno-btn is-active'
                        : 'fi-register__yesno-btn'
                    }
                    onClick={() => {
                      setCustom(field.key, 'No');
                      if (isMembershipTier) {
                        setCustom(CUSTOMER_FIRST_PLAN_ID_KEY, '');
                        setCustom(CUSTOMER_FIRST_CREDIT_KEY, '');
                      }
                    }}
                  >
                    No
                  </button>
                </div>
                {showPlanId ? (
                  <>
                    <div className="fi-register__plan-id">
                      <span className="fi-register__field-label">Plan ID</span>
                      <div className="fi-register__input-wrap">
                        <span className="fi-register__input-icon">
                          <IconNote />
                        </span>
                        <input
                          type="text"
                          value={planIdValue}
                          placeholder="Enter plan ID"
                          required
                          onChange={(e) => setCustom(CUSTOMER_FIRST_PLAN_ID_KEY, e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="fi-register__plan-id">
                      <span className="fi-register__field-label">Plan credit limit</span>
                      <div className="fi-register__input-wrap">
                        <span className="fi-register__input-icon">
                          <IconNote />
                        </span>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={creditValue}
                          placeholder="Enter credit limit"
                          onChange={(e) => setCustom(CUSTOMER_FIRST_CREDIT_KEY, e.target.value)}
                        />
                      </div>
                    </div>
                  </>
                ) : null}
              </>
            ) : inputKind === 'options' ? (
              <div className="fi-register__options" role="group" aria-label={label}>
                {optionItems.map((opt) => {
                  const optLabel = String(opt?.label || '').trim();
                  if (!optLabel) return null;
                  const iconSrc = resolveOptionIconUrl(opt, field.type);
                  const selected =
                    String(value || '').trim().toLowerCase() === optLabel.toLowerCase();
                  return (
                    <button
                      key={optLabel}
                      type="button"
                      className={
                        selected
                          ? 'fi-register__option-btn is-active'
                          : 'fi-register__option-btn'
                      }
                      aria-pressed={selected}
                      onClick={() => setCustom(field.key, optLabel)}
                    >
                      {iconSrc ? (
                        <img src={iconSrc} alt="" className="fi-register__option-icon" />
                      ) : null}
                      <span className="fi-register__option-label">{optLabel}</span>
                    </button>
                  );
                })}
              </div>
            ) : field.type === 'mobile' ? (
              <div className="fi-register__mobile-lookup">
                <div className="fi-register__input-wrap fi-register__phone">
                  <CountryWhatsAppInput
                    id={`fi-register-${field.key}`}
                    value={normalizeRegisterMobileValue(value)}
                    defaultCountry={DEFAULT_WHATSAPP_COUNTRY}
                    placeholder={field.placeholder || 'Mobile number with country code'}
                    containerClassName="fi-register__phone-input"
                    disabled={crmBusy}
                    onChange={(phoneE164) => {
                      setCrmMessage('');
                      setCustom(field.key, phoneE164);
                    }}
                  />
                </div>
                {enableNtliveMobileLookup ? (
                  <div className="fi-register__mobile-lookup-actions">
                    <button
                      type="button"
                      className="fi-register__crm-lookup-btn"
                      disabled={crmBusy}
                      onClick={async () => {
                        const e164 = normalizeRegisterMobileValue(value);
                        if (!isRegisterMobileComplete(e164)) {
                          setCrmMessage(mobileValidationError());
                          return;
                        }
                        setCrmBusy(true);
                        setCrmMessage('');
                        try {
                          const data = await lookupNtliveCustomerByMobile(e164);
                          if (data?.error) {
                            setCrmMessage(data.error);
                            return;
                          }
                          if (!data?.found || !data?.customer) {
                            setCrmMessage(
                              data?.message || 'No customer record found for this mobile number.',
                            );
                            return;
                          }
                          const { values: merged, filledLabels } = applyNtliveCustomerToRegisterValues(
                            registerForm,
                            values,
                            data.customer,
                          );
                          setValues(merged);
                          onCrmCustomerFound?.(data.customer);
                          const name = data.customer.name || data.customer.full_name || 'Customer';
                          setCrmMessage(
                            filledLabels.length
                              ? `Found ${name} — filled ${filledLabels.slice(0, 4).join(', ')}${filledLabels.length > 4 ? '…' : ''}.`
                              : `Found ${name} — review the form and save.`,
                          );
                        } catch (err) {
                          setCrmMessage(err?.message || 'CRM lookup failed');
                        } finally {
                          setCrmBusy(false);
                        }
                      }}
                    >
                      {crmBusy ? 'Checking CRM…' : 'Fetch from salon CRM'}
                    </button>
                    {crmMessage ? (
                      <p className="fi-register__crm-lookup-msg" role="status">{crmMessage}</p>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="fi-register__input-wrap">
                <span className="fi-register__input-icon">
                  <FieldIcon inputKind={inputKind} />
                </span>
                {inputKind === 'date' ? (
                  <input
                    type="date"
                    value={value}
                    placeholder={field.placeholder || ''}
                    required={field.required}
                    onChange={(e) => setCustom(field.key, e.target.value)}
                  />
                ) : inputKind === 'textarea' ? (
                  <textarea
                    rows={3}
                    value={value}
                    placeholder={field.placeholder || ''}
                    required={field.required}
                    onChange={(e) => setCustom(field.key, e.target.value)}
                  />
                ) : (
                  <input
                    type="text"
                    value={value}
                    placeholder={field.placeholder || ''}
                    required={field.required}
                    onChange={(e) => setCustom(field.key, e.target.value)}
                  />
                )}
              </div>
            )}
          </label>
        );
      })}
    </div>
  );
}
